import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import { bookingPathForVoucher, formatVoucherCodeInput } from "constants/shopVouchers";

const PRINTABLE_STATUSES = new Set(["active", "redeemed", "used"]);

const openPdfForPrint = (blob, printWindow) => {
  const file = new File([blob], "Gutschein.pdf", { type: "application/pdf" });
  const url = URL.createObjectURL(file);
  const target = printWindow && !printWindow.closed ? printWindow : window.open(url);
  if (!target) return;

  if (target.location.href !== url) target.location.replace(url);

  const openedAt = Date.now();
  let printed = false;
  const tryPrint = () => {
    if (printed || target.closed || Date.now() - openedAt < 800) return;
    let href = "";
    try {
      href = target.location.href || "";
    } catch {
      href = url;
    }
    if (!href || href === "about:blank") return;
    printed = true;
    try {
      target.focus();
      target.print();
    } catch {
      /* The PDF stays open so it can be printed from the viewer. */
    }
  };

  target.addEventListener("load", () => window.setTimeout(tryPrint, 200));
  const timer = window.setInterval(() => {
    tryPrint();
    if (printed || target.closed) {
      window.clearInterval(timer);
      if (target.closed) URL.revokeObjectURL(url);
    }
  }, 400);
  window.setTimeout(() => window.clearInterval(timer), 8000);
};

const VoucherPanel = ({ hideHead = false }) => {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [purchased, setPurchased] = useState([]);
  const [redeemed, setRedeemed] = useState([]);
  const [printingId, setPrintingId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/vouchers/mine");
    setPurchased(data?.purchased || []);
    setRedeemed(data?.redeemed || []);
  }, []);

  useEffect(() => {
    load().catch(() => {
      setStatus({ kind: "error", text: "Gutscheine konnten nicht geladen werden." });
    });
  }, [load]);

  const redeem = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const { data } = await api.post("/vouchers/redeem", { code });
      setStatus({ kind: "success", text: data?.message || "Gutschein eingelöst." });
      setCode("");
      await load();
    } catch (err) {
      setStatus({
        kind: "error",
        text: err?.response?.data?.message || "Der Gutschein konnte nicht eingelöst werden.",
      });
    } finally {
      setBusy(false);
    }
  };

  const printVoucher = async (voucher) => {
    const printWindow = window.open("", "_blank");
    setPrintingId(voucher.id);
    setStatus(null);
    try {
      const response = await api.get(`/vouchers/${voucher.id}/pdf`, { responseType: "blob" });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "application/pdf" });
      const signature = await blob.slice(0, 5).text();
      if (!signature.startsWith("%PDF")) {
        printWindow?.close();
        setStatus({ kind: "error", text: "Der Gutschein konnte nicht gedruckt werden." });
        return;
      }
      openPdfForPrint(blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" }), printWindow);
    } catch {
      printWindow?.close();
      setStatus({ kind: "error", text: "Der Gutschein konnte nicht gedruckt werden." });
    } finally {
      setPrintingId(null);
    }
  };

  const printButton = (voucher) => {
    if (!PRINTABLE_STATUSES.has(voucher.status)) return null;
    const busy = printingId === voucher.id;
    return (
      <button
        type="button"
        className="konto-voucher-print"
        disabled={busy}
        onClick={() => printVoucher(voucher)}
      >
        {busy ? "Wird vorbereitet…" : "Drucken"}
      </button>
    );
  };

  const available = redeemed.filter((voucher) => voucher.status === "redeemed");
  const used = redeemed.filter((voucher) => voucher.status === "used");

  return (
    <section
      className="konto-voucher-card"
      aria-labelledby={hideHead ? undefined : "konto-voucher-title"}
      aria-label={hideHead ? "Gutscheine" : undefined}
    >
      {!hideHead && (
        <div className="konto-referral-head">
          <h2 id="konto-voucher-title">Gutscheine</h2>
          <p>
            Löse einen Code ein. Danach kannst du ihn beim{" "}
            <Link to="/buchen">Buchen</Link> für genau diese Massage verwenden.{" "}
            <Link to="/shop">Gutschein kaufen</Link>
          </p>
        </div>
      )}
      <form className="konto-voucher-form" onSubmit={redeem}>
        <label htmlFor="voucher-code">Gutscheincode</label>
        <div>
          <input
            id="voucher-code"
            inputMode="numeric"
            autoComplete="off"
            placeholder="0000-0000-0000-0000"
            value={code}
            onChange={(event) => setCode(formatVoucherCodeInput(event.target.value))}
          />
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "Prüft…" : "Einlösen"}
          </button>
        </div>
      </form>
      {status && <div className={`auth-alert is-${status.kind}`}>{status.text}</div>}

      <div className="konto-voucher-list">
        <h3>Eingelöst</h3>
        {available.length === 0 ? (
          <p>Noch kein Gutschein eingelöst.</p>
        ) : (
          <ul>
            {available.map((voucher) => (
              <li key={voucher.id}>
                <div>
                  <strong>{voucher.label}</strong>
                  <span>{voucher.code}</span>
                  {printButton(voucher)}
                </div>
                <div className="konto-voucher-actions">
                  <Link className="btn-primary" to={bookingPathForVoucher(voucher)}>
                    Jetzt buchen
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {used.length > 0 && (
        <div className="konto-voucher-list">
          <h3>Verwendet</h3>
          <ul>
            {used.map((voucher) => (
              <li key={voucher.id}>
                <div>
                  <strong>{voucher.label}</strong>
                  <span>{voucher.code}</span>
                  {printButton(voucher)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {purchased.length > 0 && (
        <div className="konto-voucher-list">
          <h3>Gekauft</h3>
          <ul>
            {purchased.map((voucher) => (
              <li key={`bought-${voucher.id}`}>
                <div>
                  <strong>{voucher.label}</strong>
                  <span>
                    {voucher.code} · {formatPrice(voucher.priceCents)}
                    {voucher.isGift ? ` · Geschenk an ${voucher.recipientEmail}` : ""}
                  </span>
                  {printButton(voucher)}
                </div>
                <div className="konto-voucher-actions">
                  <span>
                    {voucher.status === "active"
                      ? "Offen"
                      : voucher.status === "redeemed"
                        ? "Eingelöst"
                        : voucher.status === "used"
                          ? "Verwendet"
                          : voucher.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default VoucherPanel;
